import { GetStaticProps, InferGetStaticPropsType, NextPage } from 'next';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';

import Field from '~components/ui/Field';
import Form from '~components/ui/Form';
import Input from '~components/ui/Input';
import TextArea from '~components/ui/Input/TextArea';
import Layout from '~components/ui/Layout';
import Select from '~components/ui/Select';
import { getFromAPI, postToAPI } from '~utils/api/common';
import useResponsive from '~utils/hooks/useResponsive';
import normalizeContact, { NormalizedCinemasInContact } from '~utils/normalizers/normalizeContact';

import { Cinema } from '~types';

import s from '~styles/pages/Contacto.module.css';

interface GSProps {
  cinemas: NormalizedCinemasInContact[];
}

export const getStaticProps: GetStaticProps<GSProps> = async () => {
  const cinemas = await getFromAPI<Cinema[]>('/cinemas');

  return {
    props: {
      cinemas: normalizeContact(cinemas),
    },
    revalidate: 60 * 60 * 24, // Each day
  };
};

interface FormTypes {
  name: string;
  email: string;
  subject: string;
  to: string;
  message: string;
}

type Status = 'idle' | 'readyToSubmit' | 'loading';

type Props = InferGetStaticPropsType<typeof getStaticProps>;

const Contacto: NextPage<Props> = ({ cinemas }) => {
  const form = Form.useForm();
  const router = useRouter();
  const { isDark } = useResponsive();
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState<string>();
  const [token, setToken] = useState<string | null>();

  // Se encarga de desactivar el botón si no han verificado el captcha
  useEffect(() => {
    if (typeof token !== 'string') {
      setStatus('idle');
    } else {
      setStatus('readyToSubmit');
    }
  }, [token]);

  const handleFinish = async (data: FormTypes) => {
    setStatus('loading');
    try {
      const response = await postToAPI('/contacts', data);
      if (!!response.message) {
        router.push('/contacto/gracias');
      }
    } catch (error) {
      setStatus('readyToSubmit');
      setError((error as Error).message);
    }
  };

  const onChange = (token: string | null) => {
    setToken(token);
  };

  return (
    <Layout
      meta={{ title: 'Contacto', description: 'Contacta con nuestro personal' }}
      headerType="translucent"
    >
      <div className={s.root}>
        <div className="w-full text-center">
          <h1 className="h1 sm:big-title">Contáctanos</h1>
          <p className="text-lg">Ponte en contacto con nosotros, queremos saber tu opinión</p>
        </div>
        <Form form={form} onSubmit={handleFinish} className={s.form}>
          <div className="flex w-full text-left col-span-full lg:mb-5">
            <p className="text-sm md:w-3/4">
              Todos los campos son requeridos. Tus datos solo se utilizarán para poder contactarte
              Citicinemas® no proporcionará estos datos a terceros.
            </p>
          </div>
          <Field
            label="Nombre:"
            name="name"
            rules={[{ required: true, message: 'Escribe tu nombre' }]}
          >
            <Input className={s.input} />
          </Field>
          <Field
            label="Correo electrónico:"
            name="email"
            rules={[
              { required: true, message: 'Escribe tu correo electrónico' },
              { email: true, message: 'Escribe un correo valido.' },
            ]}
          >
            <Input className={s.input} />
          </Field>
          <Field
            label="Asunto:"
            name="subject"
            rules={[{ required: true, message: 'Escribe el asunto a tratar' }]}
          >
            <Input className={s.input} />
          </Field>
          <Field
            label="Para:"
            name="to"
            rules={[{ required: true, message: 'Escribe el destinatario' }]}
          >
            <Select className={s.select}>
              <option value={process.env.NEXT_PUBLIC_CONTACT_EMAIL}>Corporativo</option>
              <option value="empleo@citicinemas.com">Bolsa de trabajo</option>
              {cinemas?.map((cinema) => (
                <option value={cinema?.managerEmail} key={cinema?.id}>
                  {cinema?.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field
            label="Mensaje:"
            name="message"
            className="col-span-full"
            rules={[{ required: true, message: 'Escribe el mensaje' }]}
          >
            <TextArea className={s.textarea} />
          </Field>
          <div className={s.captchaAndButtonContainer} key={isDark ? 'dark' : 'light'}>
            <ReCAPTCHA
              theme={isDark ? 'dark' : 'light'}
              sitekey={process.env.NEXT_PUBLIC_GOOGLE_RECAPTCHA_KEY || ''}
              onChange={(token: string | null) => onChange(token)}
            />
            <button className={s.sendButton} disabled={status !== 'readyToSubmit'}>
              {status !== 'loading' ? 'Enviar mensaje' : 'Enviando...'}
            </button>
            {!!error && <p className="text-primary">{error}</p>}
          </div>
        </Form>
        <section className="space-y-2 lg:w-[260px]">
          <h3 className="h3">Nuestros cines</h3>
          {cinemas?.map((cinema) => (
            <div className={s.cinemaContactInfo} key={cinema?.id}>
              <h4 className="text-sm font-bold">{cinema?.name}</h4>
              <div className="flex justify-around">
                <p className="w-1/5 text-[10px]">Dirección:</p>
                <p className="w-4/5 text-sm">{cinema?.address || 'No existe'}</p>
              </div>
              <div className="flex justify-evenly">
                <p className="w-1/5 text-[10px]">Teléfono:</p>
                <p className="w-4/5 text-sm">{cinema?.phoneNumbers || 'No existe'}</p>
              </div>
            </div>
          ))}
        </section>
      </div>
    </Layout>
  );
};

export default Contacto;
